import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Chips } from '~/eds/src/components/Chips/Chips';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { getLabelForDocumentCount } from '~/utils/document.utils';

import {
  MODAL_CUSTOM_FIELD,
  MODAL_CUSTOM_FIELD_MOVE,
  MODAL_DELETE,
} from '../../../types/modal.types';
import styles from './EcCustomFieldItem.styles';

class EcCustomFieldItem extends Component {
  getFieldTypes() {
    const { intl } = this.props;
    return {
      ARRAY_MULTIPLE: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeDropdown',
        defaultMessage: 'Dropdown',
        description: 'Field type for multiple selection dropdown',
      }),
      ATTACHMENT: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeAttachment',
        defaultMessage: 'Attachment',
        description: 'Field type for attachments',
      }),
      STRING: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeText',
        defaultMessage: 'Text',
        description: 'Field type for text input',
      }),
      NUMBER: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeNumber',
        defaultMessage: 'Number',
        description: 'Field type for numeric input',
      }),
      DATE: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeDate',
        defaultMessage: 'Date',
        description: 'Field type for date input',
      }),
      BOOLEAN: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeTrueFalse',
        defaultMessage: 'True/False',
        description: 'Field type for boolean input',
      }),
      REFERENCE: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeReference',
        defaultMessage: 'Reference',
        description: 'Field type for reference input',
      }),
      ARRAY_SINGLE: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeSingleDropdown',
        defaultMessage: 'Dropdown',
        description: 'Field type for single selection dropdown',
      }),
      TEXT_AREA: intl.formatMessage({
        id: 'EcCustomFieldItem.fieldTypeTextArea',
        defaultMessage: 'Text Area',
        description: 'Field type for text area input',
      }),
    };
  }

  renderExtraOptions(item) {
    const { section, handleShowModal, intl } = this.props;

    function getMenuItems() {
      const items = [];

      const isWssField = item.static_key_name;
      const isSmartField = item.is_smart;

      items.push({
        label: intl.formatMessage({
          id: 'EcCustomFieldItem.editMenuItem',
          defaultMessage: 'Edit',
          description: 'Menu item to edit custom field',
        }),
        disabled: isSmartField,
        tooltip: isSmartField
          ? intl.formatMessage({
              id: 'EcCustomFieldItem.editSmartTooltip',
              defaultMessage: 'Standard AI fields cannot be edited.',
              description:
                'Tooltip explaining why standard AI (formerly smart) fields cannot be edited',
            })
          : undefined,
        onClick: () => handleShowModal(MODAL_CUSTOM_FIELD, section, item),
      });

      if (!item.is_smart && !isWssField) {
        items.push({
          label: intl.formatMessage({
            id: 'EcCustomFieldItem.deleteMenuItem',
            defaultMessage: 'Delete',
            description: 'Menu item to delete custom field',
          }),
          onClick: () => handleShowModal(MODAL_DELETE, section, item, true),
          disabled: item.is_xray,
          tooltip: item.is_xray
            ? intl.formatMessage({
                id: 'EcCustomFieldItem.deleteXrayTooltip',
                defaultMessage:
                  'To delete a field attached to a Custom AI model, you must first delete the model under Automation.',
                description:
                  'Tooltip explaining why Custom AI field cannot be deleted',
              })
            : undefined,
        });
      }

      items.push({
        label: intl.formatMessage({
          id: 'EcCustomFieldItem.moveToSectionMenuItem',
          defaultMessage: 'Move to Section...',
          description: 'Menu item to move field to another section',
        }),
        onClick: () => handleShowModal(MODAL_CUSTOM_FIELD_MOVE, section, item),
      });

      return items;
    }

    return (
      <ActionsMenu
        id={`client_list_actions_menu?id=${item.id}`}
        actions={getMenuItems()}
        name={intl.formatMessage({
          id: 'EcCustomFieldItem.actionsMenuName',
          defaultMessage: 'client list actions menu',
          description: 'Accessibility name for custom field actions menu',
        })}
      />
    );
  }

  renderRuleTooltip(item) {
    const { intl } = this.props;

    const tooltip =
      item.doc_restriction === 'EXCLUDE' && item.doc_types.length
        ? intl.formatMessage(
            {
              id: 'EcCustomFieldItem.ruleExcludeTooltip',
              defaultMessage: 'Rule: All document types except {docTypes}',
              description:
                'Tooltip for rule excluding specific document types. Example: docTypes = "document"',
            },
            { docTypes: item.doc_types.join(', ') },
          )
        : intl.formatMessage(
            {
              id: 'EcCustomFieldItem.ruleIncludeTooltip',
              defaultMessage: 'Rule: document types include: {docTypes}',
              description:
                'Tooltip for rule including specific document types. Example: docTypes = "document"',
            },
            { docTypes: item.doc_types.join(', ') },
          );

    return (
      <Layout align="center" justify="center">
        <Text>
          {item.doc_restriction === 'EXCLUDE' && !item.doc_types.length
            ? intl.formatMessage({
                id: 'EcCustomFieldItem.allDocumentTypes',
                defaultMessage: 'All document types',
                description:
                  'Text indicating field applies to all document types',
              })
            : item.doc_restriction === 'EXCLUDE' && item.doc_types.length
              ? intl.formatMessage(
                  {
                    id: 'EcCustomFieldItem.excludedDocumentTypes',
                    defaultMessage:
                      '{count, plural, one {# document type} other {# document types}}',
                    description:
                      'Text showing count of excluded document types. Example: count = 3',
                  },
                  {
                    count: item.num_doc_types - item.doc_types.length,
                  },
                )
              : intl.formatMessage(
                  {
                    id: 'EcCustomFieldItem.includedDocumentTypes',
                    defaultMessage:
                      '{count, plural, one {# document type} other {# document types}}',
                    description:
                      'Text showing count of included document types. Example: count = 5',
                  },
                  {
                    count: item.doc_types.length,
                  },
                )}
        </Text>
        <span data-tip data-for={`ruleText-${item.id}`}>
          {item.doc_restriction === 'EXCLUDE' &&
          !item.doc_types.length ? null : (
            <Icon
              icon="info"
              label={intl.formatMessage({
                id: 'EcCustomFieldItem.infoLabel',
                defaultMessage: 'info',
                description: 'Accessibility label for info icon',
              })}
              tooltip={tooltip}
              id={`ruleText-${item.id}`}
            />
          )}
        </span>
      </Layout>
    );
  }

  render() {
    const { classes, item, dragHandle, intl } = this.props;

    const fieldTypes = this.getFieldTypes();

    const appliesToCol = getLabelForDocumentCount(
      item?.num_docs_applied_to?.file_count,
      item?.num_docs_applied_to?.document_count,
    );

    const getChips = (field) => {
      const chips = [];

      if (field.is_smart) {
        chips.push({
          text: intl.formatMessage({
            id: 'EcCustomFieldItem.smartChip',
            defaultMessage: 'Standard AI',
            description: 'Chip label for standard AI (formerly smart) fields',
          }),
          tooltip: intl.formatMessage({
            id: 'EcCustomFieldItem.smartTooltip',
            defaultMessage:
              'This field is an intelligent data field. Data will be automatically populated in this field when found by our algorithm.',
            description:
              'Tooltip explaining standard AI (formerly smart) field functionality',
          }),
        });
      }
      if (field.is_beta) {
        chips.push({ preset: 'beta' });
      }
      if (field.is_xray) {
        chips.push({
          status: 'ai',
          text: intl.formatMessage({
            id: 'EcCustomFieldItem.xrayChip',
            defaultMessage: 'Custom AI',
            description: 'Chip label for Custom AI fields',
          }),
        });
      }
      return chips;
    };

    return (
      <>
        <div className={classes.fieldRow}>
          <span className={classes.fieldNameContainer}>
            <span className={classes.fieldName}>
              {dragHandle}
              <Layout align="center" justify="center" spacing={2}>
                <Text>{item.label}</Text>
                <Chips chips={getChips(item)} />
              </Layout>
            </span>
          </span>
          <span style={{ width: '23%', display: 'flex' }}>
            <Layout align="center" justify="center" spacing={2}>
              <Text>{fieldTypes[item.field_type]}</Text>
            </Layout>
          </span>
          <span className={classes.fieldDocTypeContainer}>
            <Layout align="center" justify="center" spacing={2}>
              <Text>{this.renderRuleTooltip(item)}</Text>
            </Layout>
          </span>
          <span className={classes.fieldAppliesToContainer}>
            {appliesToCol}
          </span>
          {this.renderExtraOptions(item)}
        </div>
      </>
    );
  }
}

EcCustomFieldItem.propTypes = {
  classes: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  handleShowModal: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectSheet(styles)(injectIntl(EcCustomFieldItem));
